import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export const Eyebrow = ({ children }) => (
  <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#C9A227] font-medium block mb-4">
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, children, className = "" }) => (
  <div className={className}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug">
      {title}
    </h2>
    {children && <p className="mt-6 text-base text-zinc-400 leading-relaxed">{children}</p>}
  </div>
);

export const GoldButton = ({ to, href, variant = "solid", testid, children, className = "" }) => {
  const solid =
    "inline-flex items-center justify-center px-8 py-4 text-[12px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-colors gold-glow";
  const ghost =
    "inline-flex items-center justify-center px-8 py-4 text-[12px] uppercase tracking-[0.2em] font-medium text-[#D4AF37] card-line hover:bg-[#16161A] hover:text-[#E8C85A] transition-colors";
  const cls = `${variant === "solid" ? solid : ghost} ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  return <a href={href} data-testid={testid} className={cls}>{children}</a>;
};

export const MetricCard = ({ icon: Icon, value, label, sub, testid, delay = 0 }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay }}
    className="bg-[#0A0A0B]/90 backdrop-blur-md p-7 hover:bg-[#16161A] transition-colors group h-full"
  >
    {Icon && <Icon size={20} className="text-[#C9A227] mb-5 group-hover:text-[#E8C85A] transition-colors" />}
    <div className="font-display text-3xl text-white tracking-wide tabular-nums">{value}</div>
    <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-[#D4AF37]">{label}</div>
    {sub && <div className="mt-2 text-sm text-zinc-400">{sub}</div>}
  </motion.div>
);

export const ComplianceBadge = ({ label, detail, testid }) => (
  <div
    data-testid={testid}
    className="flex items-center gap-3 px-5 py-3.5 card-line bg-[#0A0A0B] hover:bg-[#16161A] hover:border-[#C9A227]/40 transition-colors"
  >
    <span className="w-1.5 h-1.5 rotate-45 bg-[#C9A227] shrink-0" aria-hidden="true" />
    <div>
      <div className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#E8C85A]">{label}</div>
      {detail && <div className="text-[11px] text-zinc-400 mt-0.5">{detail}</div>}
    </div>
  </div>
);

export const CommodityTile = ({ name, spec, desc, tag, testid, className = "" }) => (
  <div
    data-testid={testid}
    className={`group bg-[#0A0A0B] p-8 lg:p-10 h-full hover:bg-[#16161A] transition-colors ${className}`}
  >
    {tag && (
      <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 border border-zinc-700 px-3 py-1">
        {tag}
      </span>
    )}
    <h3 className="mt-6 font-display text-xl text-white tracking-wide group-hover:text-[#E8C85A] transition-colors">
      {name}
    </h3>
    {spec && <div className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#C9A227]">{spec}</div>}
    {desc && <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{desc}</p>}
  </div>
);

export const PageShell = ({ eyebrow, title, updated, children }) => (
  <div className="bg-[#0A0A0B] min-h-screen">
    <div className="pt-40 pb-16 hairline-b bg-[#121215]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-wide text-white leading-tight">
          {title}
        </h1>
        {updated && (
          <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.25em] text-zinc-400">{updated}</p>
        )}
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">{children}</div>
  </div>
);

export const ProseSection = ({ heading, children }) => (
  <section className="mb-12">
    <h2 className="font-display text-xl text-[#E8C85A] tracking-wide mb-4">{heading}</h2>
    <div className="space-y-4 text-[15px] text-zinc-400 leading-relaxed">{children}</div>
  </section>
);
